import { Component, Inject, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OutputsCollectionsService } from 'src/app/services/outputs-collections.service';
import {
  AssetPreviewResult,
  CollectionAssetPreviewResult,
} from 'src/app/models/outputs-collections/outputs-collections-result';
import { ReviewProductDataFilesDialogData } from 'src/app/models/outputs-collections/review-product-data-files/dialog-data';
import { ReviewProductDataFilesResult } from 'src/app/models/outputs-collections/review-product-data-files/review-product-data-files-result';
import { ReviewProductDataFilesRow } from 'src/app/models/outputs-collections/review-product-data-files/review-product-data-files-row';
import { ReviewProductDataFilesSummary } from 'src/app/models/outputs-collections/review-product-data-files/review-product-data-files-summary';
import { CannotProceedProductRow } from 'src/app/models/outputs-collections/shared/cannot-proceed-product-row';
import { FileExtension } from 'src/app/models/product-data-filename-conventions/file-extension';

@Component({
  selector: 'app-review-product-data-files-dialog',
  templateUrl: './review-product-data-files-dialog.component.html',
  styleUrls: ['./review-product-data-files-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class ReviewProductDataFilesDialogComponent implements OnInit {
  isLoading = true;
  hasErrored = false;
  result: ReviewProductDataFilesResult | null = null;
  readonly displayedColumns: string[] = ['product', 'format', 'filePath', 'exists'];
  readonly cannotProceedColumns: string[] = ['product', 'reason'];

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: ReviewProductDataFilesDialogData,
    public readonly dialogRef: MatDialogRef<ReviewProductDataFilesDialogComponent>,
    private readonly outputsCollectionsService: OutputsCollectionsService,
  ) {}

  ngOnInit(): void {
    this.loadPreview();
  }

  loadPreview(): void {
    this.outputsCollectionsService
      .previewOutputsCollection({
        productIds: this.data.scope.productIds,
        outputFormats: [],
        productDataFormats: this.data.productDataFormats,
      })
      .subscribe({
        next: (response: CollectionAssetPreviewResult) => {
          this.result = this.mapToProductDataFilesResult(response);
          this.isLoading = false;
        },
        error: () => {
          this.result = null;
          this.hasErrored = true;
          this.isLoading = false;
        },
      });
  }

  // cannotProceed[] stays empty until the backend response grows a per-product
  // reason field (follow-up: BE cannotProceed reason). Backend currently drops
  // products missing the PDFC-required identifier upstream, so no row surfaces.
  mapToProductDataFilesResult(response: CollectionAssetPreviewResult): ReviewProductDataFilesResult {
    const productDataAssets = response.assets.filter((asset) => asset.isProductData);
    const rows = productDataAssets.map((asset) => this.mapToRow(asset));
    const cannotProceed: CannotProceedProductRow[] = [];
    const summary = this.buildSummary(rows, cannotProceed);
    return { rows, cannotProceed, summary };
  }

  get hasBlockers(): boolean {
    if (this.result == null) return false;
    return this.result.summary.missingCount > 0;
  }

  getFileName(key: string): string {
    const parts = key.split('/');
    return parts[parts.length - 1];
  }

  getProductIdentifier(row: ReviewProductDataFilesRow): string {
    const fileName = this.getFileName(row.storage.key);
    const dotIndex = fileName.lastIndexOf('.');
    return dotIndex >= 0 ? fileName.substring(0, dotIndex) : fileName;
  }

  private mapToRow(asset: AssetPreviewResult): ReviewProductDataFilesRow {
    return {
      origin: {
        product: {
          id: asset.productId,
          identifiers: {},
        },
        format: {
          extension: this.parseFileExtension(asset.s3Key),
        },
      },
      storage: {
        key: asset.s3Key,
        uri: asset.s3Uri,
      },
      state: {
        exists: asset.fileExists,
        reviewState: asset.reviewState,
      },
    };
  }

  private parseFileExtension(s3Key: string): FileExtension {
    const dotIndex = s3Key.lastIndexOf('.');
    const extensionText = dotIndex >= 0 ? s3Key.substring(dotIndex + 1).toLowerCase() : '';
    if (extensionText === 'csv') return FileExtension.Csv;
    return FileExtension.Json;
  }

  private buildSummary(
    rows: ReviewProductDataFilesRow[],
    cannotProceed: CannotProceedProductRow[],
  ): ReviewProductDataFilesSummary {
    return {
      totalFiles: rows.length,
      missingCount: rows.filter((row) => !row.state.exists).length,
      cannotProceedCount: cannotProceed.length,
    };
  }
}
